import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import {LinksColumnSliceTiles} from 'prismicio-types'
import {LocalesType} from 'src/common/constants'
import {EVENTS, track} from 'src/common/helpers'
import {Icon} from 'src/common/Icon'
import cn from 'classnames'
import Link from 'next/link'

type SpotlightVariationProps = {
  slice: LinksColumnSliceTiles
  componentIndex: number
  context: {shouldHideTitle?: boolean; pageUid: string}
}

export const TilesVariation = ({slice, context: {pageUid}}: SpotlightVariationProps) => {
  const router = useRouter()
  const locale = router.locale as LocalesType
  const {linkResolver} = useLinkResolver()

  return (
    <div
      className="col-span-2 flex w-full flex-col gap-4 px-4 text-black lg:w-[40%] lg:gap-8 lg:px-6 lg:pb-8 lg:pt-6 lg:group-hover:opacity-100"
      data-slice-variation={slice.variation}
    >
      {isFilled.keyText(slice.primary.column_title) && (
        <span className="text-tg-body-l w-full">{slice.primary.column_title}</span>
      )}
      <div className="flex w-full max-w-[519px] flex-col gap-6">
        {slice.primary.tiles.map((tile, index) => {
          const tagProperties = NAV_TAG_PROPERTIES[tile.tag!]

          return (
            <Link
              key={tile.title! + index}
              className="group flex justify-between rounded-[4px] border-[1px] border-black/[10%] bg-moss-white p-3 transition-all duration-500 hover:border-[#94CC32]"
              href={linkResolver(tile.link_url)}
              onClick={() => {
                track({
                  event: EVENTS.CLICK,
                  dataType: `order-${index}`,
                  location: 'main-nav-drop-down-tiles',
                  mktgId: tile.marketing_id!,
                  pageName: pageUid,
                })
              }}
              target={tile.link_url?.target || '_self'}
            >
              <div className="flex gap-4">
                <PrismicNextImage
                  field={tile.image}
                  className={cn('h-full w-full rounded-[4px]', {
                    'max-h-[94px] max-w-[94px]': slice.primary.tiles.length > 1,
                    'max-h-[94px] max-w-[94px] lg:max-h-[135px] lg:max-w-[135px]': slice.primary.tiles.length === 1,
                  })}
                />
                <div className="flex flex-col gap-2">
                  {isFilled.select(tile.tag) && (
                    <div
                      className={`flex min-h-4 w-min items-center justify-center text-nowrap rounded-[4px] px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-[11px] tracking-[.64px] text-black ${tagProperties.backgroundColor} ${tagProperties.textColor}`}
                    >
                      {tagProperties.text[locale]}
                    </div>
                  )}
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[13.5px] leading-[17px] text-black">{tile.title}</span>
                    <span className="text-[13.5px] leading-[17px] text-black/[70%]">{tile.description}</span>
                  </div>
                </div>
              </div>
              <Icon name="arrow" className="h-2 min-w-[9px] duration-500" />
            </Link>
          )
        })}
      </div>
    </div>
  )
}

const NAV_TAG_PROPERTIES = {
  New: {
    backgroundColor: 'bg-[#CCFF3F]',
    textColor: 'text-black',
    text: {
      'en-us': 'New',
      'en-gb': 'New',
      'de-de': 'Neu',
      'nl-nl': 'Nieuw',
    },
  },
  Event: {
    backgroundColor: 'bg-moss-blue',
    textColor: 'text-moss-white',
    text: {
      'en-us': 'Event',
      'en-gb': 'Event',
      'de-de': 'Event',
      'nl-nl': 'Event',
    },
  },
  Report: {
    backgroundColor: 'bg-moss-blue',
    textColor: 'text-moss-white',
    text: {
      'en-us': 'Report',
      'en-gb': 'Report',
      'de-de': 'Bericht',
      'nl-nl': 'Rapport',
    },
  },
  Article: {
    backgroundColor: 'bg-moss-blue',
    textColor: 'text-moss-white',
    text: {
      'en-us': 'Article',
      'en-gb': 'Article',
      'de-de': 'Artikel',
      'nl-nl': 'Artikel',
    },
  },
  'Online event': {
    backgroundColor: 'bg-moss-blue',
    textColor: 'text-moss-white',
    text: {
      'en-us': 'Online Event',
      'en-gb': 'Online Event',
      'de-de': 'Online-Event',
      'nl-nl': 'Online Event',
    },
  },
}
